<template>
  <div
    class="profile-hour_rate-circle nowrap"
    style="border-radius: 100px; padding: 5px"
    @mouseover="hover = true"
    @mouseout="hover = false"
  >
    <div
      :class="{ 'editable-hover': hover && editable }"
      class="rate-edit-trigger"
      @click="handleClick"
    >
      <span
        v-if="account.hour_rate == '?'"
        :title="
          $t(
            'Once you have created a job, you will be able to view hourly rates on the freelancer.',
          )
        "
        data-toggle="tooltip"
      >
        <span class="circle circle40 circle-indigo">
          <span class="f-400 f-20 c-indigo"><i class="zmdi zmdi-help" /></span>
          &nbsp;
          {{ $store.state.user.automatic_currency_conversion ? endAccountCurrency : rateCurrency }}
        </span>
      </span>
      <div>
        <span :title="$t(title)" class="circle circle40 circle-indigo">
          {{ $store.state.user.automatic_currency_conversion ? endAccountCurrency : rateCurrency }}
        </span>
        &nbsp;
        <span class="f-700 c-indigo">
          <CurrencyDisplay
            :force-disable-conversions="isMe"
            :show-converted-rates-by-default="$store.state.user.automatic_currency_conversion"
            :currency-code="rateCurrency"
            :converted-currency-code="endAccountCurrency ? endAccountCurrency : rateCurrency"
            :rate="Number(rate)"
          />
          {{ $t(rateTypeSlash) }}
        </span>
      </div>
    </div>
    <BaseModal
      :header-text="$t('Edit your rates')"
      :modal-id="`edit-${rateType}rate-modal`"
      :is-open="showModal"
      :save-state="saveState"
      :disabled="disableSubmit"
      @opened="handleModalOpened"
      @cancel="handleModalCancel"
      @save="handleModalSave"
    >
      <InputPayRates
        :market="account.market || 'dk'"
        :market-preferred-rate-type="marketPreferredRateType"
        :currency="account.currency || 'dkk'"
        :minimum-hourly-rate="minimumHourlyRate"
        :minimum-hourly-rate-error-text="minimumHourlyRateErrorText"
        :commission-percentage="commissionPercentage"
        :persistent-hourly-rate="parseFloat(account.hour_rate) || 0"
        :persistent-daily-rate="parseFloat(account.daily_rate) || 0"
        :persistent-monthly-rate="parseFloat(account.monthly_rate) || 0"
        :force-show-monthly-rate-input="rateType === rateEnum.monthly"
        @rate-changed="handleRateChanged"
        @block-toggled="handleBlocking"
      />
    </BaseModal>
  </div>
</template>

<script>
import enums, { rate } from '@/utils/enum'
import { SET_EDIT_UNSAVED, UPDATE_USER_STATE } from '@/state/mutation-types'
import { SET_EDIT_SAVING_STATE } from '@/state/action-types'
import { NetworkError } from '@/utils/notification'
import { UPDATE_FREELANCER } from '@/state/apollo/accounts'
import CurrencyDisplay from '@/components/CurrencyDisplay.vue'
import BaseModal from '@/components/BaseModal.vue'
import InputPayRates from '@/components/InputPayRates.vue'

export default {
  name: 'EditRate',
  components: { InputPayRates, BaseModal, CurrencyDisplay },
  props: {
    isMe: { type: Boolean, required: true },
    rateCurrency: { type: String, required: true },
    endAccountCurrency: { type: String, required: false },
    rateType: { type: String, required: false, default: 'hourly' },
    minimumHourlyRate: { type: Number, required: true },
    minimumHourlyRateErrorText: { type: String, required: true },
    commissionPercentage: { type: Number, required: true },
    rateIn: { type: Number, default: 0 },
    marketPreferredRateType: {
      type: String,
      required: false,
      default: 'hourly',
    },
  },
  data() {
    return {
      initialDailyRate: 0,
      initialMonthlyRate: 0,
      initialHourlyRate: 0,
      disableSubmit: false,
      hover: false,
      showModal: false,
      saveState: 'fresh',
      hasBeenOpenedWithoutSaving: false,
    }
  },
  computed: {
    rateEnum() {
      return rate
    },
    editable() {
      return this.isMe && this.$store.state.edit.on
    },
    rate() {
      if (!this.isMe) {
        return this.rateIn
      }
      switch (this.rateType) {
        case rate.monthly:
          return this.$store.state.user.monthlyRate
        case rate.hourly:
          return this.$store.state.user.hourlyRate
        case rate.daily:
          return this.$store.state.user.dailyRate
        default:
          return 0
      }
    },
    title: {
      get() {
        switch (this.rateType) {
          case rate.monthly:
            return 'Monthly rate'
          case rate.hourly:
            return 'Hourly rate'
          case rate.daily:
            return 'Daily rate'
          default:
            return '???'
        }
      },
    },
    rateTypeSlash: {
      get() {
        switch (this.rateType) {
          case rate.monthly:
            return 'rate.monthly_slash'
          case rate.hourly:
            return 'rate.hourly_slash'
          case rate.daily:
            return 'rate.daily_slash'
          default:
            return 'rate.unknown_slash'
        }
      },
    },
  },
  created() {
    if (Number(this.account.hour_rate) > 0) {
      this.$store.commit(UPDATE_USER_STATE, {
        fieldName: 'hourlyRate',
        fieldValue: this.account.hour_rate,
      })
    }
    if (Number(this.account.daily_rate) > 0) {
      this.$store.commit(UPDATE_USER_STATE, {
        fieldName: 'dailyRate',
        fieldValue: this.account.daily_rate,
      })
    }
    if (Number(this.account.monthly_rate) > 0) {
      this.$store.commit(UPDATE_USER_STATE, {
        fieldName: 'monthlyRate',
        fieldValue: this.account.monthly_rate,
      })
    }
  },
  methods: {
    handleClick() {
      if (this.editable) {
        this.showModal = !this.showModal
      }
    },
    handleRateChanged(rateType, newRate) {
      this.$store.commit(UPDATE_USER_STATE, {
        fieldName: `${rateType}Rate`,
        fieldValue: newRate,
      })
    },
    handleBlocking(isBlocked) {
      this.disableSubmit = isBlocked
    },
    handleModalOpened() {
      this.hasBeenOpenedWithoutSaving = true
      // keep a copy of state when initially
      // opening modal in case we need to revert it
      this.initialDailyRate = this.$store.state.user.dailyRate
      this.initialMonthlyRate = this.$store.state.user.monthlyRate
      this.initialHourlyRate = this.$store.state.user.hourlyRate
      this.$store.commit(SET_EDIT_UNSAVED, true)
    },
    handleModalCancel() {
      // Restore the original state before opening the modal
      if (this.hasBeenOpenedWithoutSaving) {
        this.$store.commit(UPDATE_USER_STATE, {
          fieldName: 'dailyRate',
          fieldValue: this.initialDailyRate,
        })
        this.$store.commit(UPDATE_USER_STATE, {
          fieldName: 'monthlyRate',
          fieldValue: this.initialMonthlyRate,
        })
        this.$store.commit(UPDATE_USER_STATE, {
          fieldName: 'hourlyRate',
          fieldValue: this.initialHourlyRate,
        })
      }
      this.showModal = false
      this.$store.commit(SET_EDIT_UNSAVED, false)
    },
    async handleModalSave() {
      this.$store.dispatch(SET_EDIT_SAVING_STATE, true)
      this.saveState = 'saving'
      this.$store.commit(SET_EDIT_UNSAVED, true)
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_FREELANCER,
          variables: {
            input: {
              globalId: this.account.globalId,
              monthlyRate: this.$store.state.user.monthlyRate,
              hourRate: this.$store.state.user.hourlyRate,
              dailyRate: this.$store.state.user.dailyRate,
            },
          },
        })

        this.$store.dispatch(SET_EDIT_SAVING_STATE, false)
        this.hasBeenOpenedWithoutSaving = false
        this.$store.commit(SET_EDIT_UNSAVED, false)
        this.saveState = 'saved'
        setTimeout(() => {
          this.showModal = false
          this.saveState = 'fresh'
        }, 1000)
      } catch (e) {
        this.$store.commit(SET_EDIT_UNSAVED, true)
        this.$store.dispatch(SET_EDIT_SAVING_STATE, false)
        new NetworkError({
          severity: enums.notification.severity.CRITICAL,
          ex: e,
        }).dispatch(this)
        setTimeout(() => {
          this.saveState = 'fresh'
        }, 2000)
      }
    },
  },
}
</script>

<style scoped lang="scss">
i {
  position: absolute;
  margin-left: 5px;
}
.editable-hover {
  cursor: pointer;
}
</style>
